'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui/utils';
const PROGRESS_SCALE: number = 10_000;
const PROGRESS_SCALE_BIGINT: bigint = BigInt(PROGRESS_SCALE);
const ZERO: bigint = BigInt(0);
interface AnimatedBigIntProps {
  readonly value: bigint;
  readonly format: (value: bigint) => ReactNode;
  readonly duration?: number | undefined;
  readonly className?: string | undefined;
}

/** Keeps fixed-point amounts exact while animating mount and subsequent changes. */
export function AnimatedBigInt(t0) {
  const $ = _c(10);
  const {
    value,
    format,
    duration: t1,
    className
  } = t0;
  const duration = t1 === undefined ? 800 : t1;
  const reducedMotion = useReducedMotion();
  let t2;
  if ($[0] !== className) {
    t2 = cn("tabular-nums", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== duration || $[3] !== format || $[4] !== reducedMotion || $[5] !== value) {
    t3 = reducedMotion || duration <= 0 ? format(value) : <AnimatedBigIntFrames value={value} format={format} duration={duration} />;
    $[2] = duration;
    $[3] = format;
    $[4] = reducedMotion;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function AnimatedBigIntFrames(t0) {
  const $ = _c(7);
  const {
    value,
    format,
    duration
  } = t0;
  const [display, setDisplay] = useState(ZERO);
  const currentRef = useRef(ZERO);
  let t1;
  let t2;
  if ($[0] !== duration || $[1] !== value) {
    t1 = () => {
      const from = currentRef.current;
      if (from === value) {
        return;
      }
      const start = performance.now();
      let frameId;
      function tick(now) {
        const progress = Math.min((now - start) / duration, 1);
        const eased = BigInt(Math.round((1 - (1 - progress) ** 3) * PROGRESS_SCALE));
        const current = from + (value - from) * eased / PROGRESS_SCALE_BIGINT;
        currentRef.current = current;
        setDisplay(current);
        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        }
      }
      frameId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frameId);
    };
    t2 = [value, duration];
    $[0] = duration;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== display || $[5] !== format) {
    t3 = format(display);
    $[4] = display;
    $[5] = format;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}